import { ArrowLeft, Play } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  onboardingKindLabel,
  onboardingStatusLabel,
  onboardingStatusVariant,
} from '@/features/hrm/onboarding';
import {
  useCabinetOnboardingPlan,
  useMarkCabinetOnboardingItem,
  useStartCabinetOnboarding,
} from '../queries';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

/** My Space → Onboarding → read one + tick checklist items. */
export function CabinetOnboardingDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: plan, isPending, isError } = useCabinetOnboardingPlan(publicId);
  const mark = useMarkCabinetOnboardingItem();
  const start = useStartCabinetOnboarding();

  const back = () => navigate('/me/onboarding');

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !plan) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Onboarding
        </Button>
        <p className="text-muted-foreground">This checklist isn’t available.</p>
      </div>
    );
  }

  const done = plan.items.filter((i) => i.is_done).length;
  const pct = plan.items.length > 0 ? Math.round((done / plan.items.length) * 100) : 0;

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> Onboarding
      </Button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{onboardingKindLabel(plan.kind)}</h1>
        <Badge variant={onboardingStatusVariant(plan.status)}>
          {onboardingStatusLabel(plan.status)}
        </Badge>
        {plan.status === 'todo' ? (
          <Button
            className="ml-auto"
            onClick={() => start.mutate(plan.public_id)}
            disabled={start.isPending}
          >
            <Play /> Start
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Status">{onboardingStatusLabel(plan.status)}</Field>
          <Field label="Started">{fmtDate(plan.started_on)}</Field>
          <Field label="Completed">{fmtDate(plan.completed_on)}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Checklist</CardTitle>
          <p className="text-muted-foreground text-sm tabular-nums">
            {done}/{plan.items.length} done · {pct}%
          </p>
          <div className="bg-muted relative mt-2 h-1.5 w-full overflow-hidden rounded-full">
            <div className="bg-primary h-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </CardHeader>
        <CardContent>
          {plan.items.length === 0 ? (
            <p className="text-muted-foreground text-sm">No checklist items.</p>
          ) : (
            <ul className="divide-y">
              {plan.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span
                    className={item.is_done ? 'text-muted-foreground line-through' : 'text-foreground'}
                  >
                    {item.title}
                  </span>
                  <Switch
                    checked={item.is_done}
                    disabled={mark.isPending && mark.variables?.id === item.id}
                    onCheckedChange={(checked) => mark.mutate({ id: item.id, isDone: checked })}
                    aria-label={`Mark "${item.title}" ${item.is_done ? 'not done' : 'done'}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
